import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Template } from '../../types/template';
import { TemplatePreviewModal } from '../../components/Templates/TemplatePreviewModal';
import { TemplateTypeBadge } from '../../components/Templates/TemplateTypeBadge';
import { TemplateStatusBadge } from '../../components/Templates/TemplateStatusBadge';
import { TemplateActionButtons } from '../../components/Templates/TemplateActionButtons';
import { TemplateFilters } from '../../components/Templates/TemplateFilters';
import { TemplateLoadingSkeleton } from '../../components/Templates/TemplateLoadingSkeleton';
import { TemplateEmptyState } from '../../components/Templates/TemplateEmptyState';

export const TemplatesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Filter state
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Preview modal state
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);

  // Fetch templates
  const { data: templates = [], isLoading, error } = useQuery<Template[]>({
    queryKey: ['templates', typeFilter, statusFilter],
    queryFn: async () => {
      const params: any = {};
      if (typeFilter) params.template_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const response = await api.templates.list(params);
      const data = response.data;
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    },
  });

  // Filter templates by search query
  const filteredTemplates = templates.filter((template) =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase())
  );


  // Action handlers
  const handleView = (id: string) => {
    setPreviewTemplateId(id);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setTypeFilter('');
    setStatusFilter('');
  };


  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Templates</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create and manage email and SMS templates for your campaigns
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => navigate('/templates/new')}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Create Template
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6">
        <TemplateFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onClearFilters={handleClearFilters}
        />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="mt-8">
          <TemplateLoadingSkeleton type="table" count={5} />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mt-8">
          <TemplateEmptyState
            type="error"
            errorMessage={(error as any).message || 'An unexpected error occurred'}
          />
        </div>
      )}

      {/* Empty State - No Templates */}
      {!isLoading && !error && templates.length === 0 && !typeFilter && !statusFilter && !searchQuery && (
        <div className="mt-8">
          <TemplateEmptyState type="no-templates" />
        </div>
      )}

      {/* Empty State - No Results */}
      {!isLoading && !error && filteredTemplates.length === 0 && (typeFilter || statusFilter || searchQuery) && (
        <div className="mt-8">
          <TemplateEmptyState
            type="no-results"
            onClearFilters={handleClearFilters}
          />
        </div>
      )}

      {/* Templates Table */}
      {!isLoading && !error && filteredTemplates.length > 0 && (
        <div className="mt-8 flex flex-col">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Type
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Variables
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Created
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filteredTemplates.map((template) => (
                      <tr key={template.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">{template.name}</div>
                          {template.description && (
                            <div className="text-gray-500 truncate max-w-xs">{template.description}</div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <TemplateTypeBadge type={template.template_type} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <TemplateStatusBadge status={template.status} />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {template.variables?.length || 0} variables
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatDate(template.created_at)}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex items-center justify-end space-x-2">
                            {/* View/Preview Button */}
                            <TemplateActionButtons
                              template={template}
                              onPreview={handleView}
                              size="md"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <TemplatePreviewModal
        templateId={previewTemplateId}
        isOpen={!!previewTemplateId}
        onClose={() => setPreviewTemplateId(null)}
      />
    </div>
  );
};

